import Link from 'next/link'
import styled, { css } from 'styled-components'
import { CAD1_LIVE, CAD1_VISIBLE, cad1Paths } from 'views/Cad1/config'
import { LOCK_HOME } from 'views/LockTools/config'
import { CREATE } from 'views/TradingCompetitionV3/routes'
import type { ToolLines } from './hooks'
import { BadgeView, Chip, DISPLAY, HUES, Hue, Icon3d, MUTED, SNOW, SNOW_2, Skel, SrOnly } from './ui'

/*
 * The four tools a project uses here, as big tiles (Bez 2026-10-09: "the main page should be functional, not just a landing"). Each tile is
 * one link with a 3D icon, a name, one line, its live badge and a line under it; the badge and that line hold their size while their read
 * is in flight. CAD1 is shown as "in testing" only: no link, no button, no date. The Stage build (CAD1_VISIBLE) links the same strip to /cad1,
 * still "In testing" and with no date; production stays as it is until Bez opens the soft launch.
 */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: 1fr;
  gap: 12px;
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
  }
`

const lift = css`
  transition: transform 180ms cubic-bezier(0.22, 1, 0.36, 1), border-color 180ms ease, box-shadow 180ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
const Tile = styled(Link)<{ $hue: Hue }>`
  --rgb: ${({ $hue }) => HUES[$hue].rgb};
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 156px;
  padding: 12px 12px 10px;
  border-radius: 22px;
  color: ${SNOW};
  background: radial-gradient(110% 80% at 100% 0%, rgba(var(--rgb), 0.34) 0%, rgba(var(--rgb), 0.08) 45%, rgba(var(--rgb), 0) 70%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0) 38%), #0b1028;
  border: 1px solid rgba(var(--rgb), 0.42);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.14), inset 0 -18px 30px -24px rgba(var(--rgb), 0.45), 0 14px 32px rgba(0, 0, 0, 0.5),
    0 12px 36px -14px rgba(var(--rgb), 0.55);
  ${lift}

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(var(--rgb), 0.8);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18), inset 0 -18px 30px -24px rgba(var(--rgb), 0.6), 0 18px 40px rgba(0, 0, 0, 0.55),
      0 16px 44px -12px rgba(var(--rgb), 0.75);
  }
  &:active {
    transform: translateY(0) scale(0.985);
  }
  &:focus-visible {
    outline: 2px solid ${HUES.ice.text};
    outline-offset: 3px;
  }
  @media (prefers-reduced-motion: reduce) {
    &:hover,
    &:active {
      transform: none;
    }
  }

  .top {
    order: -1;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    min-height: 46px;
  }
  .ico {
    width: 46px;
    height: 46px;
    flex: none;
    transform: rotate(-8deg);
    filter: drop-shadow(0 8px 14px rgba(0, 0, 0, 0.45)) drop-shadow(0 0 16px rgba(var(--rgb), 0.55));
    ${lift}
  }
  &:hover .ico {
    transform: rotate(0deg) scale(1.08);
  }
  @media (prefers-reduced-motion: reduce) {
    &:hover .ico {
      transform: rotate(-8deg);
    }
  }
  h3 {
    margin: 6px 0 2px;
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 20px;
    line-height: 24px;
    letter-spacing: -0.01em;
  }
  .desc {
    margin: 0;
    min-height: 36px;
    font-size: 13px;
    line-height: 18px;
    color: ${SNOW_2};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  /* one line, or two (the Token page's team tick and gold tick); the grid gives every tile the tallest tile's height */
  .sub {
    margin-top: auto;
    padding-top: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 26px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    color: ${({ $hue }) => HUES[$hue].text};
  }
  .sub .l {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ${({ theme }) => theme.mediaQueries.md} {
    min-height: 204px;
    padding: 18px;
    .ico {
      width: 64px;
      height: 64px;
    }
    .top {
      min-height: 66px;
    }
    h3 {
      font-size: 22px;
      line-height: 28px;
      margin-top: 12px;
    }
    .desc {
      font-size: 14px;
      line-height: 20px;
      min-height: 40px;
    }
    .sub {
      font-size: 13px;
    }
  }
`

type Tool = { key: keyof ToolLines; href: string; hue: Hue; icon: string; title: string; desc: string }
export const TOOLS: Tool[] = [
  { key: 'swap', href: '/swap', hue: 'violet', icon: 'coin', title: 'Swap', desc: 'Smart Swap finds the best price.' },
  { key: 'lock', href: LOCK_HOME, hue: 'ice', icon: 'locked', title: 'Lock', desc: 'Lock tokens or LP. Anyone can check it.' },
  { key: 'compete', href: CREATE, hue: 'amber', icon: 'trophy', title: 'Compete', desc: 'Trading competitions with a prize.' },
  { key: 'pages', href: '/tokens', hue: 'blue', icon: 'glowing_star', title: 'Token page', desc: 'Price, pools, links and team.' },
]

export function ToolTiles({ lines }: { lines: ToolLines }) {
  return (
    <div>
      <SrOnly as="h2">Tools</SrOnly>
      <Grid data-testid="home-tools">
        {TOOLS.map((t) => {
          const l = lines[t.key] as { badge: any; sub: string | null; sub2?: string | null; subLoading?: boolean }
          return (
            <Tile key={t.key} href={t.href} $hue={t.hue} data-testid={`home-tool-${t.key}`}>
              {/* name first in the DOM (the link reads "Compete, Trading competitions..., 0.05 BNB"); the icon row is drawn on top */}
              <h3>{t.title}</h3>
              <p className="desc">{t.desc}</p>
              <span className="top">
                <Icon3d className="ico" name={t.icon} size={64} />
                <BadgeView b={l.badge} testId={`home-tool-${t.key}-badge`} />
              </span>
              <span className="sub" data-testid={`home-tool-${t.key}-sub`}>
                {l.subLoading ? (
                  <Skel $w="70%" $h={12} />
                ) : (
                  <>
                    {l.sub && <span className="l">{l.sub}</span>}
                    {l.sub2 && <span className="l">{l.sub2}</span>}
                  </>
                )}
              </span>
            </Tile>
          )
        })}
      </Grid>
    </div>
  )
}

const strip = `
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 64px;
  padding: 10px 14px;
  border-radius: 18px;
  border: 1px dashed rgba(${HUES.mint.rgb}, 0.5);
  background: linear-gradient(90deg, rgba(${HUES.mint.rgb}, 0.1) 0%, rgba(247, 207, 98, 0.06) 60%, rgba(247, 207, 98, 0) 100%);
  .ico {
    width: 40px;
    height: 40px;
    flex: none;
    filter: saturate(0.75) drop-shadow(0 0 10px rgba(${HUES.mint.rgb}, 0.35));
  }
  .txt {
    flex: 1;
    min-width: 0;
  }
  strong {
    display: block;
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 16px;
    line-height: 20px;
    color: ${SNOW};
  }
  .line {
    display: block;
    font-size: 13px;
    line-height: 18px;
    color: ${MUTED};
  }
`
const Strip = styled.div`
  ${strip}
`
/** the Stage build's strip: the same look, one link to /cad1 (44 px and more: the strip is 64 px tall) */
const StripLink = styled(Link)<{ $open?: boolean }>`
  ${strip}
  text-decoration: none;
  /* the launcher is open (Bez 2026-10-10): a solid green card with one clear button, not a dashed placeholder */
  ${({ $open }) =>
    $open
      ? `
  min-height: 76px;
  border: 1px solid rgba(61, 220, 132, 0.6);
  background: linear-gradient(90deg, rgba(61, 220, 132, 0.2) 0%, rgba(247, 207, 98, 0.08) 60%, rgba(5, 7, 19, 0.2) 100%);
  box-shadow: 0 8px 28px rgba(31, 157, 85, 0.22);
  .ico { filter: drop-shadow(0 0 12px rgba(61, 220, 132, 0.5)); }
  .go {
    flex: none;
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    padding: 0 16px;
    border-radius: 14px;
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 15px;
    color: #04130b;
    background: linear-gradient(180deg, #4be894 0%, #1f9d55 100%);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35);
  }
  &:hover { border-color: rgba(61, 220, 132, 0.95); }
  `
      : ''}
  transition: border-color 150ms ease;
  &:hover {
    border-color: rgba(${HUES.mint.rgb}, 0.9);
  }
  &:focus-visible {
    outline: 2px solid ${HUES.ice.text};
    outline-offset: 3px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
/**
 * CAD1 on a production build: shown, never offered. Not a link, not a button, no date (it is not deployed anywhere yet).
 * On the Stage build (CAD1_VISIBLE, inlined at build time) the strip is one link to /cad1: still "In testing", still no date, until a live
 * launcher (v2) is configured (CAD1_LIVE; Bez 2026-10-10: the test is finished): then the strip says it is open, with no testing chip.
 */
export function LaunchStrip({ linked = CAD1_VISIBLE, live = CAD1_LIVE }: { linked?: boolean; live?: boolean } = {}) {
  const open = linked && live
  const body = (
    <>
      <Icon3d className="ico" name="rocket" size={40} />
      <span className="txt">
        <strong>{open ? 'Launch a token' : 'CAD1 token launcher'}</strong>
        <span className="line">{open ? 'Token, pool and a 10-year liquidity lock in one transaction.' : linked ? 'Being tested. See how it works.' : 'Being tested. Not available yet.'}</span>
      </span>
      {open ? <span className="go">Create token</span> : <Chip $hue="rose">In testing</Chip>}
    </>
  )
  return linked ? (
    <StripLink $open={open} href={cad1Paths.home} data-testid="home-launch" aria-label={open ? 'CAD1 token launcher: launch a token' : 'CAD1 token launcher, in testing: see how it works'}>
      {body}
    </StripLink>
  ) : (
    <Strip data-testid="home-launch" aria-label="CAD1 token launcher, in testing">
      {body}
    </Strip>
  )
}
